<!-- src/lib/components/sections/Plans.svelte -->
<script lang="ts">
	import Button from '$lib/components/ui/Button.svelte';
	import { page } from '$app/stores';
	import { twMerge } from 'tailwind-merge';
	import MonthYearSwitch from '$lib/components/ui/MonthYearSwitch.svelte';

	export let memberships: Array<{
		id: string;
		code: string;
		name: string;
		description: string;
		recommended: boolean | null;
		stripe_product_id: string;
		features: Array<{ name: string }>;
		prices: {
			monthly: { id: string; unit_amount: number };
			yearly: { id: string; unit_amount: number };
		};
	}>;
	export let title = '';
	let className = '';
	export { className as class };

	let schedule: 'monthly' | 'yearly' = 'yearly';
	if ($page?.data?.user?.stripe_subscription_interval === 'month') {
		schedule = 'monthly';
	}

	$: sortedMemberships = memberships.sort((a, b) => {
		return b.prices[schedule].unit_amount - a.prices[schedule].unit_amount;
	});

	$: currentPlan = $page.data.user?.active_membership;
	$: currentPlanDetails = currentPlan ? memberships.find((m) => m.id === currentPlan.id) : null;
	$: currentSchedule =
		$page?.data?.user?.stripe_subscription_interval === 'year' ? 'yearly' : 'monthly';

	function getButtonText(membership) {
		//if (!currentPlan) return `Get ${membership.name}`;
		if (!currentPlan) return `JOIN NOW`;
		if (membership.id === currentPlan.id && schedule === currentSchedule) return 'Current Plan';
		if (canUpgrade(membership)) {
			return 'Upgrade';
		} else {
			return 'Contact Support';
		}
	}

	function isButtonDisabled(membership) {
		if (!currentPlan) return false;
		if (membership.id === currentPlan.id && schedule === currentSchedule) return true;
		return !canUpgrade(membership);
	}

	function canUpgrade(membership) {
		if (!currentPlan) return false;
		if (currentSchedule === 'monthly' && schedule === 'yearly') return true;
		if (schedule === currentSchedule) {
			return (
				membership.prices[schedule].unit_amount >
				(currentPlanDetails?.prices[schedule].unit_amount || 0)
			);
		}
		return false;
	}

	// Function to safely parse HTML
	function createHTMLString() {
		if (!title) return { __html: '' };
		return { __html: title.replace('&trade;', '™') };
	}

	function isNewFeature(feature: { name: string }, membership: { code: string }) {
		// Silver tier should never have bold features
		if (membership.code.toLowerCase() === 'silver') {
			return false;
		}

		const tiers = ['silver', 'gold', 'platinum'];
		const currentTierIndex = tiers.indexOf(membership.code.toLowerCase());

		// Get all features from previous tiers
		const previousTierFeatures = memberships
			.filter((m) => tiers.indexOf(m.code.toLowerCase()) < currentTierIndex)
			.flatMap((m) => m.features)
			.map((f) => f.name);

		// Feature is new if it doesn't exist in previous tiers
		return !previousTierFeatures.includes(feature.name);
	}
</script>

<section class={twMerge('bg-black py-32 text-white', className)}>
	{#if title}
		<h2 class="mb-20 text-center text-5xl font-bold tracking-tight">
			{@html createHTMLString().__html}
		</h2>
	{/if}

	<h3 class="text-center text-4xl">Your TWI Membership Options</h3>
	<MonthYearSwitch
		class="my-8"
		checked={schedule === 'monthly'}
		onCheckedChange={(checked) => (schedule = checked ? 'monthly' : 'yearly')}
	/>

	<div
		class="m-auto flex max-w-screen-2xl flex-col items-center justify-center gap-y-16 overflow-x-hidden"
	>
		<div class="flex max-w-[100%] justify-start lg:justify-center">
			<div
				class="no-scrollbar flex snap-x snap-mandatory items-stretch gap-6 overflow-x-auto scroll-smooth px-4"
			>
				{#each sortedMemberships as membership (membership.id)}
					<article class="flex w-[340px] shrink-0 flex-col items-center">
						{#if membership.recommended && !currentPlan}
							<div class="-mb-1.5 w-fit rounded-lg bg-platinum px-8 py-2">
								<p class="text-base font-medium uppercase tracking-wider text-white">
									Most Popular
								</p>
							</div>
						{:else if membership.id === currentPlan?.id && schedule === currentSchedule}
							<div class="-mb-1.5 w-fit rounded-lg bg-platinum px-8 py-2">
								<p class="text-base font-medium uppercase tracking-wider text-white">
									Current plan
								</p>
							</div>
						{:else}
							<div class="h-[36px]" />
							<!-- Spacer for alignment -->
						{/if}

						<div
							class="flex h-full w-full flex-col rounded-2xl
          {membership.code === 'gold'
								? 'bg-gold'
								: membership.code === 'platinum'
									? 'bg-platinum'
									: 'bg-silver'}
          p-8"
						>
							<h3 class="mb-6 text-3xl font-normal text-black">
								TWI {membership.code}
							</h3>

							<div class="mb-6 text-black">
								<div class="flex items-start gap-4">
									<div class="flex items-start">
										<span class="text-5xl font-bold">
											${membership.prices[schedule].unit_amount / 100}
										</span>
									</div>

									{#if schedule === 'yearly'}
										<div class="flex flex-col">
											<div class="text-lg text-[#FF0000] line-through">
												${(membership.prices.monthly.unit_amount * 12) / 100}
											</div>
											<div class="text-sm leading-tight text-black/80">
												${(membership.prices.monthly.unit_amount * 12 -
													membership.prices[schedule].unit_amount) /
													100} annual savings<br />
												compared to monthly
											</div>
										</div>
									{/if}
								</div>
							</div>

							<p class="mb-8 text-lg text-black/90">{membership.description}</p>

							<div
								class="-mx-8 flex-1 space-y-3 bg-white bg-opacity-40 px-8 py-6 text-base text-black"
							>
								{#each membership.features as feature}
									<div class="flex items-start gap-2">
										<span class="mt-0.5 font-bold text-black">✓</span>
										<span class={isNewFeature(feature, membership) ? 'font-bold' : ''}>
											{feature.name}
										</span>
									</div>
								{/each}
							</div>

							<form action="/api/create-checkout-session" method="POST" class="mt-8">
								<input type="hidden" name="priceId" value={membership.prices[schedule].id} />
								<input type="hidden" name="url" value={$page.url.href} />
								<Button
									type="submit"
									class="w-full rounded-full bg-black py-3 text-base font-medium uppercase tracking-wider text-white hover:bg-black/90"
									disabled={isButtonDisabled(membership)}
								>
									{getButtonText(membership)}
								</Button>
							</form>
						</div>
					</article>
				{/each}
			</div>
		</div>

		<div class="px-8 md:px-16">
			{#if $page.data.user?.stripe_subscription_status}
				<p>
					Want to manage your current subscription?
					<a href="/profile/account/subscription/billing" class="underline underline-offset-2"
						>Change here</a
					>
				</p>
			{:else if currentPlan && !$page.data.user?.stripe_subscription_status}
				<p>
					To manage or cancel your legacy account we kindly ask you to contact the <a
						href="/support/ticket"
						class="underline underline-offset-2">TWI support</a
					>!
				</p>
			{:else if !currentPlan}
				<div>
					<a href="/pricing">
						<Button size="lg" outline>Learn more</Button>
					</a>
				</div>
			{/if}
		</div>
	</div>
</section>

<style>
	.no-scrollbar::-webkit-scrollbar {
		display: none;
	}
	.no-scrollbar {
		-ms-overflow-style: none;
		scrollbar-width: none;
	}
</style>
